import type { AccountService } from '@michael-leverup/privacy-pools-core-sdk';
import { aspClient } from '@ui/services/privacyPools/aspClient';
import { PRIVACY_POOLS_ASP_ENDPOINT } from '@ui/services/privacyPools/config';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { buildHistoryData } from '../history';
import { getPoolAccountsFromAccount } from '../sdk';
import { verifyAndSanitizeSeedPhrase } from '../seedPhrase';
import { TERMINAL_CHECKPOINT_ACCOUNT_MARKER } from '../terminalCheckpoint';
import {
  type HistoryData,
  type PoolAccount,
  type ReconstructionMetadataByChainScope,
  ReviewStatus,
} from '../types';

type PrivacyPoolsAccountContextValue = {
  seed: string | null;
  accountService: AccountService | null;
  poolAccountsByChainScope: Record<string, PoolAccount[]>;
  historyData: HistoryData;
  reconstructionMetadataByChainScope: ReconstructionMetadataByChainScope;
  isLoading: boolean;
  loadError: string | null;
  loadAccount: (seedPhrase: string) => Promise<void>;
  syncPoolAccounts: () => Promise<void>;
  refreshPoolAccountsFromAccountService: () => void;
};

const PrivacyPoolsAccountContext =
  createContext<PrivacyPoolsAccountContextValue | null>(null);

const normalizeTimestamp = (timestamp: number): bigint => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 0n;

  const normalized =
    timestamp > 1_000_000_000_000
      ? Math.floor(timestamp / 1000)
      : Math.floor(timestamp);

  return BigInt(normalized);
};

const toReviewStatus = (status?: string): ReviewStatus => {
  const normalized = status?.toLowerCase();

  if (normalized === ReviewStatus.APPROVED) return ReviewStatus.APPROVED;
  if (normalized === ReviewStatus.DECLINED) return ReviewStatus.DECLINED;
  if (normalized === ReviewStatus.EXITED) return ReviewStatus.EXITED;
  if (normalized === ReviewStatus.SPENT) return ReviewStatus.SPENT;
  if (normalized === ReviewStatus.POI_REQUIRED)
    return ReviewStatus.POI_REQUIRED;

  return ReviewStatus.PENDING;
};

const resolveFinalStatus = (
  poolAccount: PoolAccount,
  reviewStatus: ReviewStatus,
): ReviewStatus => {
  if (poolAccount.ragequit) {
    return ReviewStatus.EXITED;
  }

  const isTerminalCheckpoint = Boolean(
    (poolAccount as { [TERMINAL_CHECKPOINT_ACCOUNT_MARKER]?: unknown })[
      TERMINAL_CHECKPOINT_ACCOUNT_MARKER
    ],
  );
  if (isTerminalCheckpoint) {
    return ReviewStatus.SPENT;
  }

  return reviewStatus;
};

const parseScopeKey = (
  scopeKey: string,
): {
  chainId: number;
  scope: string;
} | null => {
  const firstDashIndex = scopeKey.indexOf('-');
  if (firstDashIndex <= 0 || firstDashIndex >= scopeKey.length - 1) {
    return null;
  }

  const chainId = Number(scopeKey.slice(0, firstDashIndex));
  const scope = scopeKey.slice(firstDashIndex + 1);
  if (!Number.isFinite(chainId) || chainId <= 0 || !scope) {
    return null;
  }

  return {
    chainId,
    scope,
  };
};

export const PrivacyPoolsAccountProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [seed, setSeed] = useState<string | null>(null);
  const [accountService, setAccountService] = useState<AccountService | null>(
    null,
  );
  const [poolAccountsByChainScope, setPoolAccountsByChainScope] = useState<
    Record<string, PoolAccount[]>
  >({});
  const [historyData, setHistoryData] = useState<HistoryData>([]);
  const [
    reconstructionMetadataByChainScope,
    setReconstructionMetadataByChainScope,
  ] = useState<ReconstructionMetadataByChainScope>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const inFlightLoadRef = useRef<{
    seed: string;
    promise: Promise<void>;
  } | null>(null);
  const inFlightSyncRef = useRef<Promise<void> | null>(null);

  const syncPoolAccountsSnapshot = useCallback(
    async (
      snapshot: Record<string, PoolAccount[]>,
    ): Promise<Record<string, PoolAccount[]>> => {
      const syncedEntries = await Promise.all(
        Object.entries(snapshot).map(async ([scopeKey, accounts]) => {
          if (accounts.length === 0) {
            return [scopeKey, accounts] as const;
          }

          const parsedScope = parseScopeKey(scopeKey);
          if (!parsedScope) {
            return [scopeKey, accounts] as const;
          }

          const labels = accounts.map((account) => account.label.toString());
          if (labels.length === 0) {
            return [scopeKey, accounts] as const;
          }

          try {
            const [deposits, mtLeaves] = await Promise.all([
              aspClient.fetchDepositsByLabel(
                PRIVACY_POOLS_ASP_ENDPOINT,
                parsedScope.chainId,
                parsedScope.scope,
                labels,
              ),
              aspClient.fetchMtLeaves(
                PRIVACY_POOLS_ASP_ENDPOINT,
                parsedScope.chainId,
                parsedScope.scope,
              ),
            ]);

            const depositsByLabel = new Map(
              deposits.map((deposit) => {
                return [deposit.label, deposit] as const;
              }),
            );
            const aspLeaves = new Set(
              (mtLeaves.aspLeaves ?? []).map((leaf) => leaf.toString()),
            );

            const updatedAccounts = accounts.map((poolAccount) => {
              const matchedDeposit = depositsByLabel.get(
                poolAccount.label.toString(),
              );
              let nextReviewStatus = matchedDeposit
                ? toReviewStatus(matchedDeposit.reviewStatus)
                : poolAccount.reviewStatus;

              const isInAspLeaves = aspLeaves.has(poolAccount.label.toString());
              if (
                nextReviewStatus === ReviewStatus.APPROVED &&
                !isInAspLeaves
              ) {
                nextReviewStatus = ReviewStatus.PENDING;
              }

              const finalStatus = resolveFinalStatus(
                poolAccount,
                nextReviewStatus,
              );

              return {
                ...poolAccount,
                isValid: isInAspLeaves,
                reviewStatus: finalStatus,
                deposit: matchedDeposit
                  ? {
                      ...poolAccount.deposit,
                      timestamp: normalizeTimestamp(matchedDeposit.timestamp),
                    }
                  : poolAccount.deposit,
              };
            });

            return [scopeKey, updatedAccounts] as const;
          } catch (error) {
            console.warn('Failed to sync pool accounts for scope', {
              scopeKey,
              error,
            });

            return [scopeKey, accounts] as const;
          }
        }),
      );

      return syncedEntries.reduce<Record<string, PoolAccount[]>>(
        (accumulator, [scopeKey, accounts]) => {
          accumulator[scopeKey] = accounts;

          return accumulator;
        },
        {},
      );
    },
    [],
  );

  const commitPoolAccounts = useCallback(
    (nextPoolAccountsByChainScope: Record<string, PoolAccount[]>) => {
      setPoolAccountsByChainScope(nextPoolAccountsByChainScope);
      setHistoryData(buildHistoryData(nextPoolAccountsByChainScope));
    },
    [],
  );

  const refreshPoolAccountsFromAccountService = useCallback(() => {
    if (!accountService) {
      return;
    }

    const { poolAccountsByChainScope: nextPoolAccountsByChainScope } =
      getPoolAccountsFromAccount(accountService.account);
    commitPoolAccounts(nextPoolAccountsByChainScope);
  }, [accountService, commitPoolAccounts]);

  const loadAccount = useCallback(
    async (seedPhrase: string) => {
      const sanitizedSeed = verifyAndSanitizeSeedPhrase(seedPhrase);
      const inFlightLoad = inFlightLoadRef.current;

      if (inFlightLoad?.seed === sanitizedSeed) {
        return inFlightLoad.promise;
      }

      const promise = (async () => {
        setIsLoading(true);
        setLoadError(null);

        const { loadAccount: loadSdkAccount } = await import('../sdk');
        const loadedResult = await loadSdkAccount(sanitizedSeed);
        setSeed(sanitizedSeed);
        setAccountService(loadedResult.accountService);
        setReconstructionMetadataByChainScope(
          loadedResult.reconstructionMetadataByChainScope,
        );

        if (loadedResult.errors.length > 0) {
          console.warn('Account reconstruction completed with pool errors', {
            errors: loadedResult.errors,
          });
        }

        const syncedPoolAccountsByChainScope = await syncPoolAccountsSnapshot(
          loadedResult.poolAccountsByChainScope,
        );
        commitPoolAccounts(syncedPoolAccountsByChainScope);
      })();

      inFlightLoadRef.current = {
        seed: sanitizedSeed,
        promise,
      };

      try {
        await promise;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to load account';
        setLoadError(message);
        throw error;
      } finally {
        setIsLoading(false);
        if (inFlightLoadRef.current?.promise === promise) {
          inFlightLoadRef.current = null;
        }
      }
    },
    [commitPoolAccounts, syncPoolAccountsSnapshot],
  );

  const syncPoolAccounts = useCallback(
    async (silent = false) => {
      const nextSnapshot = accountService
        ? getPoolAccountsFromAccount(accountService.account)
            .poolAccountsByChainScope
        : poolAccountsByChainScope;
      if (Object.keys(nextSnapshot).length === 0) {
        return;
      }

      if (inFlightSyncRef.current) {
        return inFlightSyncRef.current;
      }

      const promise = (async () => {
        if (!silent) {
          setIsLoading(true);
        }
        try {
          const syncedPoolAccountsByChainScope = await syncPoolAccountsSnapshot(
            nextSnapshot,
          );
          commitPoolAccounts(syncedPoolAccountsByChainScope);
        } finally {
          if (!silent) {
            setIsLoading(false);
          }
        }
      })();

      inFlightSyncRef.current = promise;
      try {
        await promise;
      } finally {
        if (inFlightSyncRef.current === promise) {
          inFlightSyncRef.current = null;
        }
      }
    },
    [
      commitPoolAccounts,
      inFlightSyncRef,
      accountService,
      poolAccountsByChainScope,
      syncPoolAccountsSnapshot,
    ],
  );

  useEffect(() => {
    if (!seed || Object.keys(poolAccountsByChainScope).length === 0) {
      return;
    }

    const hasPendingAccounts = Object.values(poolAccountsByChainScope).some(
      (accounts) => {
        return accounts.some((poolAccount) => {
          return (
            poolAccount.reviewStatus === ReviewStatus.PENDING ||
            poolAccount.reviewStatus === ReviewStatus.POI_REQUIRED
          );
        });
      },
    );
    const intervalMs = hasPendingAccounts ? 1_000 : 10_000;
    const timer = globalThis.setInterval(() => {
      void syncPoolAccounts(true);
    }, intervalMs);

    return () => {
      globalThis.clearInterval(timer);
    };
  }, [poolAccountsByChainScope, seed, syncPoolAccounts]);

  const value = useMemo<PrivacyPoolsAccountContextValue>(() => {
    return {
      seed,
      accountService,
      poolAccountsByChainScope,
      historyData,
      reconstructionMetadataByChainScope,
      isLoading,
      loadError,
      loadAccount,
      syncPoolAccounts: async () => syncPoolAccounts(false),
      refreshPoolAccountsFromAccountService,
    };
  }, [
    accountService,
    refreshPoolAccountsFromAccountService,
    historyData,
    isLoading,
    loadAccount,
    loadError,
    poolAccountsByChainScope,
    reconstructionMetadataByChainScope,
    seed,
    syncPoolAccounts,
  ]);

  return (
    <PrivacyPoolsAccountContext.Provider value={value}>
      {children}
    </PrivacyPoolsAccountContext.Provider>
  );
};

export const usePrivacyPoolsAccount = (): PrivacyPoolsAccountContextValue => {
  const context = useContext(PrivacyPoolsAccountContext);
  if (!context) {
    throw new Error(
      'usePrivacyPoolsAccount must be used within PrivacyPoolsAccountProvider',
    );
  }

  return context;
};
